import { ConnectButton } from '@rainbow-me/rainbowkit';
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { AppShowcaseCard } from "@/components/app-showcase-card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { Link } from "react-router-dom";
import {mainConfig} from "@/configs/main.config";

// Sample data for showcase apps
const coreApps = [
  {
    id: "nft-marketplace",
    name: "NFT Marketplace",
    description: "Buy, sell, and trade NFTs on the Larissa blockchain",
    image: "/images/showcase/nft.png",
    link: mainConfig.otherLinks.nftMarketplace,
    tags: ["NFT", "Marketplace"]
  },
  {
    id: "casino-game",
    name: "Casino Game",
    description: "Blockchain-based casino gaming platform",
    image: "/images/logos/larissa-cxc.png",
    link: mainConfig.otherLinks.casinoGame,
    tags: ["Gaming", "Casino"]
  },
  {
    id: "silo-photos",
    name: "SILO Photos (Beta)",
    description: "Decentralized photo storage and management",
    image: "/images/logos/larissa-silo-photos.png",
    link: mainConfig.otherLinks.siloPhotos,
    tags: ["Storage", "Photos"]
  },
  {
    id: "silo-drive",
    name: "SILO Drive - Beta",
    description: "Decentralized cloud storage solution",
    image: "/images/logos/larissa-silo-drive.png",
    link: mainConfig.otherLinks.siloDrive,
    tags: ["Storage", "Drive"]
  },
  {
    id: "larissa-trading-bot",
    name: "Larissa Trading Bot",
    description: "Automated trading bot powered by a ledger-based system",
    image: "/images/logos/coinmarketcap.png",
    link: "https://bot.larissa.network/",
    tags: ["Trading Bot", "Ledger"]
  }
];

const partnerApps = [
  {
    id: "vibeo",
    name: "Vibeo - AI Short-Videos",
    description: "AI-powered short video platform by Craftology Tech",
    image: "/images/logos/larissa-vibeo.png",
    link: mainConfig.otherLinks.vibeo,
    tags: ["AI", "Video", "Social"]
  },
  {
    id: "kyklos",
    name: "Kyklos",
    description: "Decentralized network infrastructure",
    image: "/images/logos/larissa-kyklos.png",
    link: mainConfig.otherLinks.kyklos,
    tags: ["Network", "Infrastructure"]
  },
  {
    id: "kyklos-basic-trading-bot",
    name: "Kyklos Basic Trading Bot",
    description: "Basic trading bot experience by Kyklos",
    image: "/images/logos/coinmarketcap.png",
    link: mainConfig.otherLinks.kyklos,
    tags: ["Trading Bot", "Basic"]
  },
  {
    id: "optilics",
    name: "Optilics",
    description: "Leading Crypto Payment Gateway",
    image: "/images/logos/larissa-optilics.png",
    link: mainConfig.otherLinks.optilics,
    tags: ["Payment", "Gateway"]
  }
];

export default function BlockchainShowcasePage() {
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/blockchain">Blockchain</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Showcase</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                <h1 className="text-3xl font-bold">Application Showcase</h1>
              </div>

              <p className="text-muted-foreground mb-8 max-w-3xl">
                Discover the growing ecosystem of applications built on the Larissa blockchain.
                From official core applications to innovative partner projects, explore what's
                possible with Larissa technology.
              </p>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              <Tabs defaultValue="all" className="mb-8">
                <TabsList className="mb-6">
                  <TabsTrigger value="all">All Applications</TabsTrigger>
                  <TabsTrigger value="core">Larissa Core</TabsTrigger>
                  <TabsTrigger value="partners">Ecosystem Partners</TabsTrigger>
                </TabsList>

                <TabsContent value="all">
                  <div className="mb-8">
                    <div className="flex items-center mb-4">
                      <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                      <h2 className="text-2xl font-bold">Larissa Core</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {coreApps.map(app => (
                        <AppShowcaseCard key={app.id} app={app} />
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center mb-4">
                      <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                      <h2 className="text-2xl font-bold">Ecosystem Partners</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {partnerApps.map(app => (
                        <AppShowcaseCard key={app.id} app={app} />
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="core">
                  <div className="flex items-center mb-4">
                    <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                    <h2 className="text-2xl font-bold">Larissa Core</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {coreApps.map(app => (
                      <AppShowcaseCard key={app.id} app={app} />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="partners">
                  <div className="flex items-center mb-4">
                    <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                    <h2 className="text-2xl font-bold">Ecosystem Partners</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {partnerApps.map(app => (
                      <AppShowcaseCard key={app.id} app={app} />
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}





